import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { Box, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import type {
	CobblesPickerCrew,
	CobblesPickerProductSize,
	CobblesPickerProductType,
} from "../models/CobblesReport";

interface Props {
	start: Moment;
	end: Moment;
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	productTypeId: number | null;
	productSizeId: number | null;
	crewId: string | null;
	onProductTypeChange: (id: number | null) => void;
	onProductSizeChange: (id: number | null) => void;
	onCrewChange: (id: string | null) => void;
	productTypes: CobblesPickerProductType[];
	productSizes: CobblesPickerProductSize[];
	crews: CobblesPickerCrew[];
	onExportExcel: () => void;
	onReset: () => void;
	canReset: boolean;
}

export const CobblesReportToolbar = ({
	start,
	end,
	onStartChange,
	onEndChange,
	productTypeId,
	productSizeId,
	crewId,
	onProductTypeChange,
	onProductSizeChange,
	onCrewChange,
	productTypes,
	productSizes,
	crews,
	onExportExcel,
	onReset,
	canReset,
}: Props) => {
	// Cascading filter: when a Product Type is selected, restrict the Product
	// (Size) dropdown to sizes belonging to that type.
	const filteredSizes =
		productTypeId == null
			? productSizes
			: productSizes.filter((s) => s.ProductTypeId === productTypeId);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 1.5,
				py: 0.75,
				flexWrap: "wrap",
			}}
		>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="Start"
					format="MM/DD/YYYY"
					value={start}
					maxDate={end}
					onChange={onStartChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="End"
					format="MM/DD/YYYY"
					value={end}
					minDate={start}
					onChange={onEndChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>

			<Box sx={{ width: 180 }}>
				<TextField
					select
					size="small"
					label="Product Type"
					fullWidth
					value={productTypeId == null ? "" : String(productTypeId)}
					onChange={(e) => {
						const v = e.target.value;
						onProductTypeChange(v === "" ? null : Number(v));
						// Clear Product Size if the previously selected size no longer
						// matches the newly selected type.
						if (productSizeId != null) {
							const stillValid = productSizes.some(
								(s) =>
									s.Id === productSizeId &&
									(v === "" || s.ProductTypeId === Number(v)),
							);
							if (!stillValid) onProductSizeChange(null);
						}
					}}
				>
					<MenuItem value="">All</MenuItem>
					{productTypes.map((pt) => (
						<MenuItem key={pt.Id} value={String(pt.Id)}>
							{pt.Name}
						</MenuItem>
					))}
				</TextField>
			</Box>

			<Box sx={{ width: 240 }}>
				<TextField
					select
					size="small"
					label="Product"
					fullWidth
					value={productSizeId == null ? "" : String(productSizeId)}
					onChange={(e) => {
						const v = e.target.value;
						onProductSizeChange(v === "" ? null : Number(v));
					}}
				>
					<MenuItem value="">All</MenuItem>
					{filteredSizes.map((ps) => (
						<MenuItem key={ps.Id} value={String(ps.Id)}>
							{ps.Name}
						</MenuItem>
					))}
				</TextField>
			</Box>

			<Box sx={{ width: 110 }}>
				<TextField
					select
					size="small"
					label="Shift"
					fullWidth
					value={crewId ?? ""}
					onChange={(e) => {
						const v = e.target.value;
						onCrewChange(v === "" ? null : v);
					}}
				>
					<MenuItem value="">All</MenuItem>
					{crews.map((c) => (
						<MenuItem key={c.Id} value={c.Id}>
							{c.Id}
						</MenuItem>
					))}
				</TextField>
			</Box>

			<ResetFiltersButton onClick={onReset} disabled={!canReset} />

			<Box sx={{ flexGrow: 1, minWidth: 8 }} />

			<Box sx={{ width: 200 }}>
				<ExcelButton handleExportToExcel={onExportExcel} />
			</Box>
		</Box>
	);
};
